interface RetryConfig {
  maxRetries: number;
  delayMs: number;
  backoffFactor: number;
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  delayMs: 1000,
  backoffFactor: 2
};

export class ApiClient {
  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    config: RetryConfig = defaultRetryConfig,
    attempt = 1
  ): Promise<T> {
    try {
      return await requestFn();
    } catch (error) {
      if (attempt >= config.maxRetries) throw error;

      const delay = config.delayMs * Math.pow(config.backoffFactor, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return this.retryRequest(requestFn, config, attempt + 1);
    }
  }

  async get<T>(url: string, options?: RequestInit): Promise<T> {
    return this.retryRequest(async () => {
      const response = await fetch(url, {
        ...options,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    });
  }

  async post<T>(url: string, data: any, options?: RequestInit): Promise<T> {
    return this.retryRequest(async () => {
      const response = await fetch(url, {
        ...options,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    });
  }
}

export const apiClient = new ApiClient();